// @flow strict
import { useEffect, useState } from 'react';

/**
 * This module provides access to the user's location coordinates.
 *
 * @author kbuzdin
 * @author sbourdeau (ported to web components March 2018)
 * @since May 2017
 */

/**
 * Check if this client can request a geolocation
 */
export const canRequestLocation = (): boolean =>
  typeof navigator !== 'undefined' && typeof navigator.geolocation !== 'undefined';

/**
 * Request a geolocation.
 * @param onSuccess A callback function if/when a geolocation is found
 * @param onFailure A callback if/when an error occurs
 */
export const requestLocation = (
  onSuccess: (latitude: number, longitude: number) => void,
  onFailure: (error: Error | PositionError) => void,
) => {
  if (!onSuccess || typeof onSuccess !== 'function') {
    return;
  }
  if (canRequestLocation()) {
    navigator.geolocation.getCurrentPosition(
      response => {
        onSuccess(response.coords.latitude, response.coords.longitude);
      },
      error => {
        if (typeof onFailure === 'function') {
          onFailure(error);
        }
      },
    );
  } else if (onFailure && typeof onFailure === 'function') {
    onFailure(new Error('Location services not available'));
  }
};

/**
 * Request a geolocation and update on change in position - currently does not allow cached locations to be returned
 * @param onSuccess A callback function if/when a geolocation is found, called automatically each time the position of the device changes
 * @param onFailure A callback if/when an error occurs
 */
export const watchLocation = (
  onSuccess: (latitude: number, longitude: number) => void,
  onFailure: (error: Error | PositionError) => void,
) => {
  if (!onSuccess || typeof onSuccess !== 'function') {
    return null;
  }

  const positionOptions = {
    maximumAge: 0,
    timeout: Infinity,
    enableHighAccuracy: false,
  };

  if (canRequestLocation()) {
    const id = navigator.geolocation.watchPosition(
      response => {
        onSuccess(response.coords.latitude, response.coords.longitude);
      },
      error => {
        if (typeof onFailure === 'function') {
          onFailure(error);
        }
      },
      positionOptions,
    );
    return id;
  } else if (onFailure && typeof onFailure === 'function') {
    onFailure(new Error('Location services not available'));
  }

  return null;
};

/**
 * Unregister the location monitoring handler from watchLocation
 * @param id from watchLocation
 */

export const clearWatch = (id: number) => {
  if (canRequestLocation()) {
    navigator.geolocation.clearWatch(id);
  }
};

/**
 * @see https://developer.mozilla.org/en-US/docs/Web/API/PositionError
 */
const PositionErrorTypes = {
  '1': 'PERMISSION_DENIED',
  '2': 'POSITION_UNAVAILABLE',
  '3': 'TIMEOUT',
};

type PositionErrorType = $Values<typeof PositionErrorTypes>;

/**
 * A React hook that returns the current geolocation
 *
 * @param enableHighAccuracy - see "https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions/enableHighAccuracy"
 * @param timeout - see "https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions/timeout"
 * @param maximumAge - see "https://developer.mozilla.org/en-US/docs/Web/API/PositionOptions/maximumAge"
 * @param pause - if true, do not request a geolocation. This can be used to control when the permission request is shown.
 */
export const useGeoLocation = ({
  enableHighAccuracy = false,
  timeout = Infinity,
  maximumAge = 0,
  pause = false,
}: {|
  enableHighAccuracy?: boolean,
  timeout?: number,
  maximumAge?: number,
  pause?: boolean,
|}): {|
  latitude: ?number,
  longitude: ?number,
  errorCode: ?PositionErrorType,
|} => {
  const [coordinates, setCoordinates] = useState<?Coordinates>(null);
  const [errorCode, setErrorCode] = useState<?PositionErrorType>(null);

  useEffect(() => {
    if (pause) {
      return () => {};
    }

    if (!canRequestLocation()) {
      setErrorCode('POSITION_UNAVAILABLE');
      return () => setErrorCode(null);
    }

    const id = navigator.geolocation.watchPosition(
      position => setCoordinates(position.coords),
      error => setErrorCode(PositionErrorTypes[error.code]),
      { enableHighAccuracy, timeout, maximumAge },
    );

    return () => clearWatch(id);
  }, [enableHighAccuracy, timeout, maximumAge, pause]);

  return {
    latitude: coordinates?.latitude,
    longitude: coordinates?.longitude,
    errorCode,
  };
};

export default requestLocation;
